import secureApi from '../../utils/secureApi';
import imageCompression from 'browser-image-compression';

export interface PlayerSeason {
  _id: string;
  player: string;
  team?: string;
  tournament: string;
  tournamentSeason: string;
  ageCategory: string;
}

export interface PlayerRecord {
  _id: string;
  name: string;
  playerSeasonInfo?: PlayerSeason;
}

function getCompressionOptions(file: File) {
  const sizeKB = file.size / 1024;
  const maxWidthOrHeight = 1600;
  if (sizeKB < 120) {
    return null;
  } else {
    return { maxSizeMB: 0.15, maxWidthOrHeight, initialQuality: 0.92, useWebWorker: true };
  }
}

export async function listPlayers({ search = '', page = 1, pageSize = 10, tournament = '', team = '', ageCategory = '', tournamentSeason, sport, matchId }: { search?: string; page?: number; pageSize?: number; tournament?: string; team?: string; ageCategory?: string; tournamentSeason?: string; sport?: string; matchId?: string }) {
  const params: any = { page, pageSize };
  
  // Always include tournament
  params.tournament = tournament;

  if (search) {
    params.search = search;
  }

  if (team) {
    params.team = team;
  }

  if (ageCategory) {
    params.ageCategory = ageCategory;
  }
  
  // If tournamentSeason is provided, also include it
  if (tournamentSeason) {
    params.tournamentSeason = tournamentSeason;
  }
  
  // If sport is provided, also include it
  if (sport) {
    params.sport = sport;
  }

  if (matchId) {
    params.matchId = matchId;
  }
  
  const response = await secureApi.get<any>(
    '/api/v2/tournaments/admin/players/get-all-players',
    { params }
  );
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data && (response.data.paginatedResults || response.data.totalCount !== undefined)) {
    return response.data;
  } else if (response && (response.paginatedResults || response.totalCount !== undefined)) {
    return response;
  }
  return response;
}

export async function getPlayerById(playerId: string, tournamentSeason?: string, sport?: string) {
  let url = `/api/v2/tournaments/admin/players/get-by-id/${playerId}`;
  
  const queryParams = new URLSearchParams();
  // Add tournamentSeason as query parameter if provided
  if (tournamentSeason) {
    queryParams.append('tournamentSeason', tournamentSeason);
  }
  // Add sport as query parameter if provided
  if (sport) {
    queryParams.append('sport', sport);
  }
  
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  
  const response = await secureApi.get<any>(url);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  // secureApi returns response.data from axios, which is the full API response
  if (response && response.data && (response.data._id || response.data.name)) {
    return response.data;
  } else if (response && (response._id || response.name)) {
    // Response is already the player data
    return response;
  } else {
    // Fallback: return response as-is
    return response;
  }
}

export async function addPlayer(payload: any, sport?: string) {
  console.log('addPlayer API called with payload:', payload);
  const url = '/api/v2/tournaments/admin/players/add';

  const sanitizedPayload: any = payload && typeof payload === 'object' ? { ...payload } : payload;
  if (sanitizedPayload && typeof sanitizedPayload === 'object' && !sanitizedPayload.dob) {
    delete sanitizedPayload.dob;
  }

  const params: Record<string, string> = {};
  if (sport) {
    params.sport = sport;
  }
  console.log('API URL:', url, 'params:', params);
  
  const response = await secureApi.post<any>(
    url,
    sanitizedPayload,
    Object.keys(params).length ? { params } : undefined
  );
  console.log('addPlayer API response:', response);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

export async function editPlayer(id: string, payload: any, sport?: string) {
  console.log('editPlayer API called with:', { id, payload });
  let url = `/api/v2/tournaments/admin/players/edit/${id}`;
  if (sport) {
    url += `?sport=${encodeURIComponent(sport)}`;
  }
  console.log('API URL:', url);
  
  const response = await secureApi.post<any>(url, payload);
  console.log('editPlayer API response:', response);
  
  // Handle API response structure: { statusCode: 200, status: "success", data: {...} }
  if (response && response.data) {
    return response.data;
  }
  return response;
}

export async function deletePlayer(id: string, sport: string) {
  const url = `/api/v2/tournaments/admin/players/delete/${id}?sport=${encodeURIComponent(sport)}`;
  return await secureApi.post<any>(url, {});
}

export async function uploadPlayerProfilePic(file: File): Promise<string> {
  let processedFile = file;
  const options = getCompressionOptions(file);
  if (options) {
    processedFile = await imageCompression(file, options);
  }
  const ext = processedFile.type.split('/')[1] || 'jpg';
  const fileName = `${Date.now()}.${ext}`;
  const context = `profile`;
  const presignRes = await secureApi.post<any>(
    '/api/v1/general/presigned-url',
    {
      fileName,
      contentType: processedFile.type,
      context,
    }
  );
  const presignedUrl = presignRes?.presignedUrl;
  const s3Key = presignRes?.key;
  if (!presignedUrl || !s3Key) {
    throw new Error('Failed to get presigned URL');
  }
  const uploadRes = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': processedFile.type },
    body: processedFile,
  });
  if (!uploadRes.ok) {
    throw new Error('Failed to upload image to S3');
  }
  return s3Key;
} 
